/* eslint-disable react-hooks/exhaustive-deps */
import { Card, Tabs, Table, Button, Space, DatePicker, Spin, Modal } from 'antd';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactECharts from 'echarts-for-react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import _ from 'lodash';

import usecustomStyles from '../../Common/customStyles';
import * as Thunk from '../../slices/statistics/thunk';

const customStyles = usecustomStyles();
const dateFormat = d => moment(d).format('YYYY-MM-DD HH:mm');

// 基础报警列定义
const baseAlarmColumns = [
    {
        title: '设备倾斜报警',
        dataIndex: 'overturnCount',
        key: 'overturnCount',
        align: 'center',
        render: (text) => parseInt(text) || 0,
        width: 100,
    },
    {
        title: '风速过大预警',
        dataIndex: 'windSpeedCount',
        key: 'windSpeedCount',
        align: 'center',
        render: (text) => parseInt(text) || 0,
        width: 100,
    },
    {
        title: '力矩过大预警',
        dataIndex: 'torqueCount',
        key: 'torqueCount',
        align: 'center',
        render: (text) => parseInt(text) || 0,
        width: 100,
    },
    {
        title: '人员进入危险区域',
        dataIndex: 'personInDangerCount',
        key: 'personInDangerCount',
        align: 'center',
        render: (text) => parseInt(text) || 0,
        width: 130,
    },
    {
        title: '驾驶员离开监督范围',
        dataIndex: 'driverLeaveCount',
        key: 'driverLeaveCount',
        align: 'center',
        render: (text) => parseInt(text) || 0,
        width: 150,
    },
    {
        title: '安全员离开监督范围',
        dataIndex: 'safetyOfficerLeaveCount',
        key: 'safetyOfficerLeaveCount',
        align: 'center',
        render: (text) => parseInt(text) || 0,
        width: 150,
    },
    {
        title: '司索工离开监督范围',
        dataIndex: 'riggerLeaveCount',
        key: 'riggerLeaveCount',
        align: 'center',
        render: (text) => parseInt(text) || 0,
        width: 150,
    },
];

// 车辆维度表格列（弹窗中使用）
const vehicleColumns = [
    {
        title: '项目名称',
        dataIndex: 'projectName',
        key: 'projectName',
        ellipsis: true,
        align: 'center',
        width: 120,
    },
    {
        title: '工区名称',
        dataIndex: 'siteName',
        key: 'siteName',
        ellipsis: true,
        align: 'center',
        width: 120,
    },
    {
        title: '车牌号',
        dataIndex: 'vehicleName',
        key: 'vehicleName',
        render: (text, record) => <Link to={`/vehicles/${record.id}?tab=location`}>{text}</Link>,
        ellipsis: true,
        align: 'center',
        width: 100,
    },
    ...baseAlarmColumns,
];

// 折线图颜色配置
const chartColors = [
    '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#eb2f96', '#13c2c2', '#fa8c16',
];

const DailyOnlineVehicleAndAlarms = ({ style }) => {
    const user = useSelector(state => state.Login.user);
    const dailyAlarmData = useSelector(state => state.Statistics.alarmsDailyCountProject);
    const projectData = useSelector(state => state.Statistics.alarmsTypeCountProject);
    const siteData = useSelector(state => state.Statistics.alarmsTypeCountSite);
    const vehicleData = useSelector(state => state.Statistics.alarmsTypeCountVehicle);
    const [chartOption, setChartOption] = useState({});
    const [dimension, setDimension] = useState('vehicle');
    const [tableData, setTableData] = useState([]);
    const [activeTab, setActiveTab] = useState('1');
    const [selectedDate, setSelectedDate] = useState(null);
    const [loading, setLoading] = useState(false);
    
    // 弹窗相关状态
    const [modalVisible, setModalVisible] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalData, setModalData] = useState([]);
    const [modalFilter, setModalFilter] = useState({ type: '', id: '' });
    const [modalDate, setModalDate] = useState(null);

    const dispatch = useDispatch();

    // 获取当前日期（支持 null 值，默认今天）
    const getCurrentDate = useCallback(() => {
        if (selectedDate && moment.isMoment(selectedDate) && selectedDate.isValid()) {
            return selectedDate;
        }
        return moment();
    }, [selectedDate]);

    // 生成请求参数
    const getPayload = useCallback(() => {
        const date = getCurrentDate();
        return {
            startDate: date.startOf('day').format('YYYY-MM-DD'),
            endDate: date.format('YYYY-MM-DD'),
        };
    }, [getCurrentDate]);

    // 获取不同维度的表格列
    const getColumnsByDimension = (dimension) => {
        const projectColumns = [
            {
                title: '项目名称',
                dataIndex: 'projectName',
                key: 'projectName',
                ellipsis: true,
                align: 'center',
                width: 120,
                render: (text, record) => (
                    <span style={{ color: '#1890ff', cursor: 'pointer' }} onClick={() => handleProjectClick(record)}>
                        {text}
                    </span>
                ),
            },
            ...baseAlarmColumns,
        ];

        const siteColumns = [
            {
                title: '项目名称',
                dataIndex: 'projectName',
                key: 'projectName',
                ellipsis: true,
                align: 'center',
                width: 120,
            },
            {
                title: '工区名称',
                dataIndex: 'siteName',
                key: 'siteName',
                ellipsis: true,
                align: 'center',
                width: 120,
                render: (text, record) => (
                    <span style={{ color: '#1890ff', cursor: 'pointer' }} onClick={() => handleSiteClick(record)}>
                        {text}
                    </span>
                ),
            },
            ...baseAlarmColumns,
        ];

        const vehicleCol = [
            {
                title: '项目名称',
                dataIndex: 'projectName',
                key: 'projectName',
                ellipsis: true,
                align: 'center',
                width: 120,
            },
            {
                title: '工区名称',
                dataIndex: 'siteName',
                key: 'siteName',
                ellipsis: true,
                align: 'center',
                width: 120,
            },
            {
                title: '车牌号',
                dataIndex: 'vehicleName',
                key: 'vehicleName',
                render: (text, record) => <Link to={`/vehicles/${record.id}?tab=location`}>{text}</Link>,
                ellipsis: true,
                align: 'center',
                width: 100,
            },
            ...baseAlarmColumns,
        ];

        switch (dimension) {
            case 'project':
                return projectColumns;
            case 'site':
                return siteColumns;
            case 'vehicle':
                return vehicleCol;
            default:
                return projectColumns;
        }
    };

    // 点击项目名称
    const handleProjectClick = (record) => {
        setModalTitle(`${record.projectName} - 车辆统计`);
        setModalFilter({ type: 'project', id: record.id });
        
        // 使用上一层级选中的日期，如果没有则用今天
        const currentDate = selectedDate && selectedDate.format ? selectedDate : moment();
        setModalDate(currentDate);
        setModalVisible(true);
        
        const payload = {
            startDate: currentDate.startOf('day').format('YYYY-MM-DD'),
            endDate: currentDate.format('YYYY-MM-DD'),
            projectId: record.id,
        };
        
        dispatch(Thunk.alarmsTypeCountVehicle(payload)).then((result) => {
            const data = result?.payload || [];
            setModalData(Array.isArray(data) ? data.map((item, index) => ({
                key: index,
                id: item.id,
                projectName: item.projectName,
                siteName: item.siteName,
                vehicleName: item.vehicleName,
                overturnCount: item.overturnCount,
                windSpeedCount: item.windSpeedCount,
                torqueCount: item.torqueCount,
                personInDangerCount: item.personInDangerCount,
                driverLeaveCount: item.driverLeaveCount,
                safetyOfficerLeaveCount: item.safetyOfficerLeaveCount,
                riggerLeaveCount: item.riggerLeaveCount,
            })) : []);
        });
    };
    
    const handleSiteClick = (record) => {
        setModalTitle(`${record.siteName} - 车辆统计`);
        setModalFilter({ type: 'site', id: record.id });
        
        // 使用上一层级选中的日期，如果没有则用今天
        const currentDate = selectedDate && selectedDate.format ? selectedDate : moment();
        setModalDate(currentDate);
        setModalVisible(true);
        
        const payload = {
            startDate: currentDate.startOf('day').format('YYYY-MM-DD'),
            endDate: currentDate.format('YYYY-MM-DD'),
            siteId: record.id,
        };
        
        dispatch(Thunk.alarmsTypeCountVehicle(payload)).then((result) => {
            const data = result?.payload || [];
            setModalData(Array.isArray(data) ? data.map((item, index) => ({
                key: index,
                id: item.id,
                projectName: item.projectName,
                siteName: item.siteName,
                vehicleName: item.vehicleName,
                overturnCount: item.overturnCount,
                windSpeedCount: item.windSpeedCount,
                torqueCount: item.torqueCount,
                personInDangerCount: item.personInDangerCount,
                driverLeaveCount: item.driverLeaveCount,
                safetyOfficerLeaveCount: item.safetyOfficerLeaveCount,
                riggerLeaveCount: item.riggerLeaveCount,
            })) : []);
        });
    };

    // 弹窗日期变化
    const handleModalDateChange = (date) => {
        setModalDate(date);
    
        // 既然 format() 方法可用，直接使用它来格式化日期
        const startDate = date?.startOf('day')?.format('YYYY-MM-DD') || moment().startOf('day').format('YYYY-MM-DD');
        const endDate = date?.format('YYYY-MM-DD') || moment().format('YYYY-MM-DD');
        const payload = {
            startDate,
            endDate,
        };
        
        if (modalFilter.type === 'project') {
            payload.projectId = modalFilter.id;
        } else if (modalFilter.type === 'site') {
            payload.siteId = modalFilter.id;
        }
        
        dispatch(Thunk.alarmsTypeCountVehicle(payload)).then((result) => {
            const data = result?.payload || [];
            setModalData(Array.isArray(data) ? data.map((item, index) => ({
                key: index,
                id: item.id,
                projectName: item.projectName,
                siteName: item.siteName,
                vehicleName: item.vehicleName,
                overturnCount: item.overturnCount,
                windSpeedCount: item.windSpeedCount,
                torqueCount: item.torqueCount,
                personInDangerCount: item.personInDangerCount,
                driverLeaveCount: item.driverLeaveCount,
                safetyOfficerLeaveCount: item.safetyOfficerLeaveCount,
                riggerLeaveCount: item.riggerLeaveCount,
            })) : []);
        });
    };

    // 初始化加载
    useEffect(() => {
        const payload = getPayload();

        // 只有当 dailyAlarmData 为空时才查询
        if (!Array.isArray(dailyAlarmData) || dailyAlarmData.length === 0) {
            dispatch(Thunk.alarmsDailyCountProject(payload));
        }

        // 只有当 projectData 为空时才查询
        if (!Array.isArray(projectData) || projectData.length === 0) {
            dispatch(Thunk.alarmsTypeCountProject(payload));
        }
        
        // 只有当 siteData 为空时才查询
        if (!Array.isArray(siteData) || siteData.length === 0) {
            dispatch(Thunk.alarmsTypeCountSite(payload));
        }
        
        // 只有当 vehicleData 为空时才查询
        if (!Array.isArray(vehicleData) || vehicleData.length === 0) {
            dispatch(Thunk.alarmsTypeCountVehicle(payload));
        }
    }, []);

    // 日期变化时立即调用接口
    const handleDateChange = useCallback((date) => {
        setSelectedDate(date);
        setLoading(true);
        
        const payload = {
            startDate: date?.startOf('day').format('YYYY-MM-DD') || moment().startOf('day').format('YYYY-MM-DD'),
            endDate: date?.format('YYYY-MM-DD') || moment().format('YYYY-MM-DD'),
        };
        
        Promise.all([
            dispatch(Thunk.alarmsDailyCountProject(payload)),
            dispatch(Thunk.alarmsTypeCountProject(payload)),
            dispatch(Thunk.alarmsTypeCountSite(payload)),
            dispatch(Thunk.alarmsTypeCountVehicle(payload)),
        ]).finally(() => {
            setLoading(false);
        });
    }, [dispatch]);

    // 维度切换
    const handleDimensionChange = useCallback((dim) => {
        setDimension(dim);
    }, []);

    // 监听数据变化更新表格
    useEffect(() => {
        let currentData = [];
        switch (dimension) {
            case 'project':
                currentData = Array.isArray(projectData) ? projectData : [];
                break;
            case 'site':
                currentData = Array.isArray(siteData) ? siteData : [];
                break;
            case 'vehicle':
                currentData = Array.isArray(vehicleData) ? vehicleData : [];
                break;
            default:
                currentData = [];
        }

        if (!currentData || currentData.length === 0) {
            setTableData([]);
            return;
        }

        setTableData(currentData.map((item, index) => ({
            key: index,
            id: item.id,
            projectName: item.projectName,
            siteName: item.siteName,
            vehicleName: item.vehicleName,
            overturnCount: item.overturnCount,
            windSpeedCount: item.windSpeedCount,
            torqueCount: item.torqueCount,
            personInDangerCount: item.personInDangerCount,
            driverLeaveCount: item.driverLeaveCount,
            safetyOfficerLeaveCount: item.safetyOfficerLeaveCount,
            riggerLeaveCount: item.riggerLeaveCount,
        })));
    }, [dimension, projectData, siteData, vehicleData]);

    // 图表数据处理 - 多项目折线图
    useEffect(() => {
        if (!Array.isArray(dailyAlarmData) || dailyAlarmData.length === 0) {
            setChartOption({
                backgroundColor: 'transparent',
                tooltip: { trigger: 'axis' },
                grid: { top: 20, right: 20, bottom: 40, left: 60 },
                xAxis: { type: 'time', axisLabel: { formatter: '{MM}-{DD}' } },
                yAxis: { type: 'value' },
                dataZoom: [
                    { type: 'inside', xAxisIndex: 0, start: 0, end: 100 },
                    { type: 'slider', xAxisIndex: 0, start: 0, end: 100, bottom: 10, height: 20 },
                ],
                series: [],
            });
            return;
        }

        const projects = _.groupBy(dailyAlarmData, 'projectName');
        const projectNames = Object.keys(projects);
        
        const allDates = [...new Set(dailyAlarmData.map(item => item.alarmDate))].sort();
        
        const series = projectNames.map((projectName, index) => {
            const projectData = projects[projectName];
            const dataMap = _.fromPairs(projectData.map(item => [item.alarmDate, item.alarmCount]));
            
            const data = allDates.map(date => ({
                name: moment(date).format('YYYY-MM-DD'),
                value: [date, dataMap[date] || 0],
            }));

            return {
                name: projectName,
                type: 'line',
                smooth: true,
                color: chartColors[index % chartColors.length],
                data: data,
            };
        });

        setChartOption({
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'axis',
                backgroundColor: 'rgb(25,57,78)',
                borderColor: 'rgb(41, 182, 230)',
                borderWidth: 0.5,
                textStyle: { fontSize: 12 },
                formatter: (params) => {
                    let res = `<div style="color: rgb(195,231,255)">${params[0]?.name}</div><ul>`;
                    params.forEach((item) => {
                        res += `<li style="color: ${item.color}">${item.seriesName}: ${item.value[1]}次</li>`;
                    });
                    return res + '</ul>';
                },
            },
            legend: {
                data: projectNames,
                textStyle: { color: '#fff' },
                top: 5,
            },
            grid: { top: 40, right: 20, bottom: 60, left: 60 },
            xAxis: {
                type: 'time',
                axisLine: { lineStyle: { color: '#666' } },
                axisLabel: {
                    color: '#ccc',
                    formatter: function(value) {
                        return moment(value).format('MM-DD');
                    },
                },
            },
            yAxis: {
                type: 'value',
                axisLine: { show: true, lineStyle: { color: '#666' } },
                axisLabel: { color: '#ccc' },
                splitLine: { lineStyle: { color: '#333' } },
            },
            dataZoom: [
                {
                    type: 'inside',
                    xAxisIndex: 0,
                    start: 0,
                    end: 100,
                },
                // {
                //     type: 'slider',
                //     xAxisIndex: 0,
                //     start: 0,
                //     end: 100,
                //     bottom: 10,
                //     height: 20,
                //     borderColor: '#1890ff',
                //     fillerColor: 'rgba(24, 144, 255, 0.2)',
                //     handleStyle: { color: '#1890ff' },
                // },
            ],
            series: series,
        });
    }, [dailyAlarmData]);

    return (
        <Card
            style={style}
            bodyStyle={{ 
                padding: '0 10px',           // 移除 Card 默认内边距
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
            }}
        >
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                tabBarStyle={{ 
                    marginBottom: 0,
                    borderBottom: '1px solid #1890ff'
                }}
                tabBarExtraContent={
                    activeTab === '1' && (
                        <Space>
                            <DatePicker
                                value={selectedDate}
                                onChange={handleDateChange}
                                placeholder="选择日期"
                                format="YYYY-MM-DD"
                            />
                            <Button.Group>
                                <Button 
                                    type={dimension === 'project' ? 'primary' : 'default'}
                                    onClick={() => handleDimensionChange('project')}
                                >
                                    项目维度
                                </Button>
                                <Button 
                                    type={dimension === 'site' ? 'primary' : 'default'}
                                    onClick={() => handleDimensionChange('site')}
                                >
                                    工区维度
                                </Button>
                                <Button 
                                    type={dimension === 'vehicle' ? 'primary' : 'default'}
                                    onClick={() => handleDimensionChange('vehicle')}
                                >
                                    车辆维度
                                </Button>
                            </Button.Group>
                        </Space>
                    )
                }
                items={[
                    {
                        key: '1',
                        label: '作业设备报警统计（计次）',
                        children: (
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Spin spinning={loading}>
                                    <Table
                                        dataSource={tableData}
                                        columns={getColumnsByDimension(dimension)}
                                        pagination={{ pageSize: 5 }}
                                        size="small"
                                        scroll={{ y: 200 }}
                                    />
                                </Spin>
                            </div>
                        ),
                    },
                    {
                        key: '2',
                        label: '各项目报警趋势对比',
                        children: (
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <ReactECharts 
                                    style={{ flex: 1, minHeight: 0 }} 
                                    option={chartOption} 
                                    notMerge={true} 
                                    lazyUpdate={true} 
                                />
                            </div>
                        ),
                    },
                ]}
            />

            {/* 弹窗 */}
            <Modal
                title={modalTitle}
                visible={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setModalData([]); // 关闭时清空数据
                }}
                width={1000}
                footer={null}
            >
                <div style={{ marginBottom: 16 }}>
                    <DatePicker
                        value={modalDate}
                        onChange={handleModalDateChange}
                        placeholder="选择日期"
                        allowClear={false}
                    />
                </div>
                <Table
                    columns={vehicleColumns}
                    dataSource={modalData}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        pageSizeOptions: ['5', '10', '20'],
                        showSizeChanger: true,
                        showTotal: (total, range) => `共 ${total} 条记录`,
                    }}
                    size="small"
                />
            </Modal>
        </Card>
    );
};

export default DailyOnlineVehicleAndAlarms;